import { useState } from 'react'
import { Laptop, Smartphone } from 'lucide-react'
import { Badge, Button, Card, ConfirmDialog } from '@/components/ui'
import { EmptyState } from '@/components/common/EmptyState'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { SectionLoader } from '@/components/common/SectionLoader'
import {
  useRevokeSession,
  useSessions,
  useSignOutOtherSessions,
} from '@/features/settings/hooks/useSessions'
import { formatDate, formatRelative } from '@/lib/formatters'
import type { ActiveSession } from '@/types/api.types'

const MOBILE_SYSTEMS = new Set(['Android', 'iOS'])

/** Sessions from before devices were recorded have nothing to show. */
function deviceName({ deviceInfo: { browser, os } }: ActiveSession): string {
  if (browser && os) return `${browser} on ${os}`
  return browser ?? os ?? 'Unknown device'
}

export function SessionsList(): React.ReactElement {
  const { data: sessions = [], isLoading, error, refetch } = useSessions()
  const revoke = useRevokeSession()
  const signOutOthers = useSignOutOtherSessions()
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (isLoading) return <SectionLoader variant="list" rows={3} label="Loading sessions" />
  if (error) {
    return <QueryErrorState error={error} title="Couldn't load sessions" onRetry={refetch} />
  }
  if (sessions.length === 0) {
    return (
      <Card className="border-gray-200/80 shadow-sm dark:border-gray-700/80">
        <EmptyState
          icon={Laptop}
          title="No sessions found"
          description="You have no active sessions."
        />
      </Card>
    )
  }

  const otherCount = sessions.filter((session) => !session.isCurrent).length

  return (
    <div className="space-y-3">
      <Card className="overflow-hidden border-gray-200/80 shadow-sm dark:border-gray-700/80">
        <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {sessions.map((session) => {
            const Icon = MOBILE_SYSTEMS.has(session.deviceInfo.os ?? '') ? Smartphone : Laptop
            return (
              <li
                key={session.sessionId}
                className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {deviceName(session)}
                      {session.isCurrent ? <Badge variant="success">This device</Badge> : null}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {session.deviceInfo.ip ? `${session.deviceInfo.ip} · ` : ''}
                      Active {formatRelative(session.lastActivityAt)} · Signed in{' '}
                      {formatDate(session.createdAt, 'PP')}
                    </p>
                  </div>
                </div>
                {session.isCurrent ? null : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full shrink-0 whitespace-nowrap sm:w-auto"
                    aria-label={`Sign out ${deviceName(session)}`}
                    loading={revoke.isPending && revoke.variables === session.sessionId}
                    disabled={revoke.isPending || signOutOthers.isPending}
                    onClick={() => revoke.mutate(session.sessionId)}
                  >
                    Sign out
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      </Card>

      {otherCount > 0 ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full whitespace-nowrap sm:w-auto"
            disabled={revoke.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            Sign out all other devices
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() =>
          signOutOthers.mutate(undefined, { onSettled: () => setConfirmOpen(false) })
        }
        title="Sign out all other devices?"
        message={`This ends ${otherCount} other ${otherCount === 1 ? 'session' : 'sessions'}. You stay signed in here.`}
        confirmLabel="Sign out"
        confirmLoading={signOutOthers.isPending}
      />
    </div>
  )
}

SessionsList.displayName = 'SessionsList'
